import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { LogOut, Trash2, Mail, Phone, Building2, Filter, RefreshCcw } from "lucide-react";
import useSeo from "../hooks/useSeo";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_LABEL = {
    new: "Novo",
    contacted: "Contatado",
    qualified: "Qualificado",
    won: "Ganho",
    lost: "Perdido",
};
const STATUS_COLOR = {
    new: "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/[0.05]",
    contacted: "text-blue-300 border-blue-400/40 bg-blue-400/[0.05]",
    qualified: "text-yellow-300 border-yellow-400/40 bg-yellow-400/[0.05]",
    won: "text-emerald-300 border-emerald-400/40 bg-emerald-400/[0.05]",
    lost: "text-red-300 border-red-400/40 bg-red-400/[0.05]",
};

export default function AdminDashboard() {
    useSeo({
        title: "Painel · Leads · GreenDuck",
        description: "Painel administrativo GreenDuck.",
        canonical: "https://www.greenduck.com.br/admin",
        noindex: true,
    });
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    async function load() {
        setLoading(true);
        try {
            const [me, l, s] = await Promise.all([
                api.get("/auth/me"),
                api.get("/admin/leads"),
                api.get("/admin/stats"),
            ]);
            setUser(me.data);
            setLeads(l.data);
            setStats(s.data);
        } catch (e) {
            navigate("/admin/login");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function logout() {
        try {
            await api.post("/auth/logout");
        } catch (_) {
            // ignore
        }
        localStorage.removeItem("gd_token");
        navigate("/admin/login");
    }

    async function updateStatus(id, status) {
        await api.patch(`/admin/leads/${id}`, { status });
        setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
        if (selected?.id === id) setSelected({ ...selected, status });
        const s = await api.get("/admin/stats");
        setStats(s.data);
    }

    async function remove(id) {
        if (!window.confirm("Excluir este lead permanentemente?")) return;
        await api.delete(`/admin/leads/${id}`);
        setLeads((ls) => ls.filter((l) => l.id !== id));
        if (selected?.id === id) setSelected(null);
        const s = await api.get("/admin/stats");
        setStats(s.data);
    }

    const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

    return (
        <div className="min-h-screen w-full bg-[#050505] text-white flex">
            {/* Sidebar */}
            <aside className="w-[260px] hidden lg:flex flex-col border-r border-white/10 p-6 sticky top-0 h-screen">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <span className="duck-dot" />
                    <span className="font-display uppercase tracking-tight text-lg">
                        GreenDuck
                    </span>
                </Link>
                <div className="text-xs font-mono-tech text-white/40 uppercase tracking-widest mb-4">
                    Filtros
                </div>
                <nav className="space-y-1 mb-8">
                    <FilterBtn
                        label="Todos"
                        count={stats?.total || 0}
                        active={filter === "all"}
                        onClick={() => setFilter("all")}
                        testid="filter-all"
                    />
                    {STATUS_OPTIONS.map((s) => (
                        <FilterBtn
                            key={s}
                            label={STATUS_LABEL[s]}
                            count={stats?.by_status?.[s] || 0}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            testid={`filter-${s}`}
                        />
                    ))}
                </nav>
                <div className="mt-auto border-t border-white/10 pt-6 space-y-3">
                    <div className="text-xs text-white/50 font-mono-tech truncate">
                        {user?.email}
                    </div>
                    <button
                        onClick={logout}
                        className="btn-ghost w-full justify-center text-sm"
                        data-testid="admin-logout"
                    >
                        <LogOut className="w-4 h-4" /> Sair
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0">
                <div className="border-b border-white/10 px-6 md:px-10 py-6 flex items-center justify-between flex-wrap gap-4 sticky top-0 bg-[#050505]/80 backdrop-blur-xl z-10">
                    <div>
                        <div className="font-mono-tech text-xs text-[#00FF66] uppercase tracking-widest">
                            Painel · Leads
                        </div>
                        <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-1">
                            {filtered.length}{" "}
                            <span className="text-white/40">
                                {filter === "all"
                                    ? "leads no total"
                                    : STATUS_LABEL[filter]}
                            </span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            className="btn-ghost text-sm"
                            data-testid="admin-refresh"
                        >
                            <RefreshCcw className="w-4 h-4" /> Atualizar
                        </button>
                    </div>
                </div>

                <div className="px-6 md:px-10 py-8">
                    {/* Mobile filter chips */}
                    <div className="lg:hidden flex gap-2 overflow-x-auto pb-4 mb-4 border-b border-white/10">
                        <Chip
                            label={`Todos (${stats?.total || 0})`}
                            active={filter === "all"}
                            onClick={() => setFilter("all")}
                        />
                        {STATUS_OPTIONS.map((s) => (
                            <Chip
                                key={s}
                                label={`${STATUS_LABEL[s]} (${stats?.by_status?.[s] || 0})`}
                                active={filter === s}
                                onClick={() => setFilter(s)}
                            />
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-white/50 py-20 text-center font-mono-tech">
                            Carregando...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="border border-dashed border-white/10 p-16 text-center">
                            <Filter className="w-8 h-8 text-white/30 mx-auto mb-4" />
                            <div className="text-white/60">
                                Nenhum lead encontrado.
                            </div>
                        </div>
                    ) : (
                        <div className="border border-white/10 clip-frame-sm overflow-hidden">
                            <table className="w-full text-sm" data-testid="leads-table">
                                <thead className="bg-white/[0.03] text-white/50 font-mono-tech text-xs uppercase tracking-widest">
                                    <tr className="border-b border-white/10">
                                        <th className="text-left px-4 py-4">Nome</th>
                                        <th className="text-left px-4 py-4 hidden md:table-cell">Contato</th>
                                        <th className="text-left px-4 py-4 hidden lg:table-cell">Empresa</th>
                                        <th className="text-left px-4 py-4">Serviço</th>
                                        <th className="text-left px-4 py-4">Status</th>
                                        <th className="text-left px-4 py-4 hidden md:table-cell">Data</th>
                                        <th className="px-4 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((l) => (
                                        <tr
                                            key={l.id}
                                            className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer"
                                            onClick={() => setSelected(l)}
                                            data-testid={`lead-row-${l.id}`}
                                        >
                                            <td className="px-4 py-4">
                                                <div className="text-white font-medium">{l.name}</div>
                                                <div className="text-white/40 text-xs md:hidden">{l.email}</div>
                                                {l.utm?.utm_source && (
                                                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono-tech text-[#00FF66]/80 uppercase tracking-widest">
                                                        ↳ {l.utm.utm_source}
                                                        {l.utm.utm_campaign && ` · ${l.utm.utm_campaign}`}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 hidden md:table-cell text-white/70">
                                                <div>{l.email}</div>
                                                {l.phone && <div className="text-white/40 text-xs mt-0.5">{l.phone}</div>}
                                            </td>
                                            <td className="px-4 py-4 hidden lg:table-cell text-white/70">
                                                {l.company || "—"}
                                            </td>
                                            <td className="px-4 py-4 text-white/70">{l.service || "—"}</td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-block text-xs font-mono-tech uppercase tracking-widest border px-2.5 py-1 rounded-full ${STATUS_COLOR[l.status] || ""}`}>
                                                    {STATUS_LABEL[l.status] || l.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 hidden md:table-cell text-white/50 font-mono-tech text-xs">
                                                {new Date(l.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        remove(l.id);
                                                    }}
                                                    className="text-white/40 hover:text-red-400 transition-colors"
                                                    data-testid={`lead-delete-${l.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>

            {/* Detail drawer */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
                    onClick={() => setSelected(null)}
                >
                    <div
                        className="w-full max-w-lg bg-[#0a0a0a] border-l border-white/10 h-full overflow-y-auto p-8"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="lead-detail-drawer"
                    >
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="font-mono-tech text-xs text-[#00FF66] uppercase tracking-widest">
                                    Lead recebido em{" "}
                                    {new Date(selected.created_at).toLocaleString("pt-BR")}
                                </div>
                                <h3 className="font-display uppercase text-3xl mt-2 tracking-tight">
                                    {selected.name}
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-white/50 hover:text-white text-2xl leading-none"
                                data-testid="close-lead-detail"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-3 mb-8">
                            <InfoRow icon={Mail} label="E-mail" value={selected.email} />
                            <InfoRow icon={Phone} label="Telefone" value={selected.phone || "—"} />
                            <InfoRow icon={Building2} label="Empresa" value={selected.company || "—"} />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-8">
                            <MiniStat label="Serviço" value={selected.service || "—"} />
                            <MiniStat label="Orçamento" value={selected.budget || "—"} />
                        </div>
                        <div className="mb-8">
                            <div className="font-mono-tech text-xs uppercase tracking-widest text-white/40 mb-2">
                                Mensagem
                            </div>
                            <p className="text-white/85 leading-relaxed border border-white/10 bg-black/40 p-4 whitespace-pre-line">
                                {selected.message}
                            </p>
                        </div>
                        {selected.utm && Object.keys(selected.utm).length > 0 && (
                            <div className="mb-8">
                                <div className="font-mono-tech text-xs uppercase tracking-widest text-[#00FF66] mb-2">
                                    Origem · UTM
                                </div>
                                <div className="border border-[#00FF66]/25 bg-[#00FF66]/[0.03] divide-y divide-white/5">
                                    {Object.entries(selected.utm).map(([k, v]) => (
                                        <div
                                            key={k}
                                            className="flex items-start justify-between gap-4 px-4 py-2 text-sm"
                                        >
                                            <span className="text-white/50 font-mono-tech text-xs uppercase tracking-widest shrink-0">
                                                {k}
                                            </span>
                                            <span className="text-white break-all text-right">
                                                {String(v)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mb-6">
                            <div className="font-mono-tech text-xs uppercase tracking-widest text-white/40 mb-3">
                                Status
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {STATUS_OPTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => updateStatus(selected.id, s)}
                                        className={`text-xs font-mono-tech uppercase tracking-widest border px-3 py-1.5 rounded-full transition-colors ${
                                            selected.status === s
                                                ? STATUS_COLOR[s]
                                                : "border-white/15 text-white/50 hover:border-white/40"
                                        }`}
                                        data-testid={`status-${s}`}
                                    >
                                        {STATUS_LABEL[s]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-3 pt-6 border-t border-white/10">
                            <a
                                href={`mailto:${selected.email}`}
                                className="btn-neon text-sm flex-1 justify-center"
                                data-testid="lead-reply-email"
                            >
                                Responder por e-mail
                            </a>
                            {selected.phone && (
                                <a
                                    href={`https://wa.me/${selected.phone.replace(/\D/g, "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-ghost text-sm"
                                >
                                    WhatsApp
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterBtn({ label, count, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                active
                    ? "bg-[#00FF66]/10 text-[#00FF66]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.03]"
            }`}
        >
            <span>{label}</span>
            <span className="font-mono-tech text-xs">{count}</span>
        </button>
    );
}

function Chip({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`shrink-0 text-xs font-mono-tech uppercase tracking-widest border px-3 py-1.5 rounded-full transition-colors ${
                active
                    ? "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/5"
                    : "border-white/15 text-white/60"
            }`}
        >
            {label}
        </button>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <Icon className="w-4 h-4 text-white/40" />
            <div className="flex-1 min-w-0">
                <div className="text-xs text-white/40 font-mono-tech uppercase tracking-widest">
                    {label}
                </div>
                <div className="text-white truncate">{value}</div>
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="border border-white/10 p-4">
            <div className="text-xs text-white/40 font-mono-tech uppercase tracking-widest mb-1">
                {label}
            </div>
            <div className="text-white font-display text-lg tracking-tight">
                {value}
            </div>
        </div>
    );
}
